import ApiUtils from './ApiUtils';
import TypeUtils from './TypeUtils';
import axios from 'axios';

export default class FormatUtils {

  public static formatMissing() {
    return 'Missing';
  }

  public static formatId(id: string) {
    return ApiUtils.hasValue(id) && id.length >= 12
        ? id.substring(0, 13).toUpperCase() : id;
  }

  public static getLocale() {
    if ((navigator as any).userLanguage) {
      return (navigator as any).userLanguage;
    } else if (navigator.languages && navigator.languages.length) {
      return navigator.languages[0];
    } else if (navigator.language) {
      return navigator.language;
    } else {
      return 'en-US';
    }
  }

  public static formatCurrency(num: any, currency: string) {
    const locale = this.getLocale();
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    });
    return formatter.format(TypeUtils.toFloat(num, 0));
  }

  // Slugify Code from: https://hagemann.io
  public static formatSlug(val: string) {
    const a = 'àáäâãåăæąçćčđďèéěėëêęğǵḧìíïîįłḿǹńňñòóöôœøṕŕřßşśšșťțùúüûǘůűūųẃẍÿýźžż·/_,:;';
    const b = 'aaaaaaaaacccddeeeeeeegghiiiiilmnnnnooooooprrsssssttuuuuuuuuuwxyyzzz------';
    const p = new RegExp(a.split('').join('|'), 'g');

    return TypeUtils.toString(val).toLowerCase()
        .replace(/\s+/g, '-')     // Replace spaces with -
        .replace(p, (c: any) => b.charAt(a.indexOf(c))) // Replace special characters
        .replace(/&/g, '-and-')   // Replace & with 'and'
        .replace(/[^\w\-]+/g, '') // Remove all non-word characters
        .replace(/\-\-+/g, '-')   // Replace multiple - with single -
        .replace(/^-+/, '')       // Trim - from start of text
        .replace(/-+$/, '');      // Trim - from end of text
  }

  /**
   * Get the Youtube Video id.
   * @param {string} str - the url from which you want to extract the id
   * @returns {string|undefined}
   */

  public static parseVideoType(str) {
    if (typeof str !== 'string') { return; }

    str = str.trim();
    str = str.replace('-nocookie', '');
    str = str.replace('/www.', '/');

    if (/\/\/google/.test(str)) {
      const matches = str.match(/url=([^&]+)&/);

      if (matches) { str = decodeURIComponent(matches[1]); }
    }

    if (/youtube|youtu\.be|y2u\.be|i.ytimg\./.test(str)) {
      return 'youtube';
    } else if (/vimeo/.test(str)) {
      return 'vimeo';
    }
  }

  public static parseVideoURL(str) {
    if (this.parseVideoType(str) === 'youtube') {
      return `https://www.youtube.com/embed/${this.parseYoutubeURL(str)}`;
    } else if (this.parseVideoType(str) === 'vimeo') {
      return `https://player.vimeo.com/video/${this.parseVimeoURL(str)}`;
    }
  }

  public static parseYoutubeURL(url) {
    // shortcode
    const shortcode = /youtube:\/\/|https?:\/\/youtu\.be\/|http:\/\/y2u\.be\//g;

    if (shortcode.test(url)) {
      const shortcodeid = url.split(shortcode)[1];
      return this.stripParameters(shortcodeid);
    }

    // /v/ or /vi/
    const inlinev = /\/v\/|\/vi\//g;

    if (inlinev.test(url)) {
      const inlineid = url.split(inlinev)[1];
      return this.stripParameters(inlineid);
    }

    // v= or vi=
    const parameterv = /v=|vi=/g;

    if (parameterv.test(url)) {
      const arr = url.split(parameterv);
      return arr[1].split('&')[0];
    }

    // v= or vi=
    const parameterwebp = /\/an_webp\//g;

    if (parameterwebp.test(url)) {
      const webp = url.split(parameterwebp)[1];
      return this.stripParameters(webp);
    }

    // embed
    const embedreg = /\/embed\//g;

    if (embedreg.test(url)) {
      const embedid = url.split(embedreg)[1];
      return this.stripParameters(embedid);
    }

    // ignore /user/username pattern
    const usernamereg = /\/user\/([a-zA-Z0-9]*)$/g;

    if (usernamereg.test(url)) { return undefined; }


    const userreg = /\/user\/(?!.*videos)/g;

    if (userreg.test(url)) {
      const elements = url.split('/');
      return this.stripParameters(elements.pop());
    }

    // attribution_link
    const attrreg = /\/attribution_link\?.*v%3D([^%&]*)(%26|&|$)/;

    if (attrreg.test(url)) { return url.match(attrreg)[1]; }
  }

  /**
   * Get the vimeo id.
   * @param {string} str - the url from which you want to extract the id
   * @returns {string|undefined}
   */
  public static parseVimeoURL(str) {
    if (str.indexOf('#') > -1) {
      str = str.split('#')[0];
    }
    if (str.indexOf('?') > -1 && str.indexOf('clip_id=') === -1) {
      str = str.split('?')[0];
    }

    let id;
    let arr;

    const vimeoPipe = [
      'https?:\/\/vimeo\.com\/[0-9]+$',
      'https?:\/\/player\.vimeo\.com\/video\/[0-9]+$',
      'https?:\/\/vimeo\.com\/channels',
      'groups',
      'album',
    ].join('|');

    const vimeoRegex = new RegExp(vimeoPipe, 'gim');

    if (vimeoRegex.test(str)) {
      arr = str.split('/');
      if (arr && arr.length) {
        id = arr.pop();
      }
    } else if (/clip_id=/gim.test(str)) {
      arr = str.split('clip_id=');
      if (arr && arr.length) {
        id = arr[1].split('&')[0];
      }
    }

    return id;
  }

  /**
   * Strip away any parameters following `?` or `/`
   * @param str
   * @returns {*}
   */
  public static stripParameters(str) {
    // Split parameters or split folder separator
    if (str.indexOf('?') > -1) {
      return str.split('?')[0];
    } else if (str.indexOf('/') > -1) {
      return str.split('/')[0];
    }
    return str;
  }

}
